import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { graphql } from '../../../client/gql';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useConfirm } from '../../../hooks/useConfirm';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { showUserDbErrorToast } from '../../../utils/toasts';

const updateProfileSettingsMutationDocument = graphql(`
  mutation BpUpdateProfileSettingsInGroupPersonsSubpage($where: ProfileWhere!, $update: UpdateProfileSettings!) {
    updateProfileSettings(where: $where, update: $update) {
      profiles {
        uuid
        __typename
        threemaConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              enabled
            }
          }
        }
        nextcloudConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              enabled
            }
          }
        }
      }
    }
  }
`);

type UsePersonBulkActionsProps = {
  freeThreemaLicenseCount?: number | null;
  onSuccess?: () => void;
};

export const usePersonBulkActions = ({ freeThreemaLicenseCount, onSuccess }: UsePersonBulkActionsProps) => {
  const { t } = useTranslation();
  const { threemaLicense } = useContext(OrganizationConfigContext);

  const [selectedPersonUuids, setSelectedPersonUuids] = useState<string[]>([]);
  const [, updateProfileSettings] = useMutation(updateProfileSettingsMutationDocument);

  const { ConfirmationDialog: ConnectionConfirmationDialog, confirm: confirmConnectionDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('common.connection'),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const { ConfirmationDialog: ActivateNextcloudConfirmationDialog, confirm: confirmActivateNextcloud } = useConfirm({
    defaultTitle: t('externalServices.createCloudAccount'),
    defaultMessage: t('externalServices.confirm.createCloudAccount'),
    defaultConfirmText: t('common.confirm'),
  });

  const handlePersonSelect = (uuid: string) => {
    setSelectedPersonUuids((prev) => {
      if (prev.includes(uuid)) {
        return prev.filter((personUuid) => personUuid !== uuid);
      }
      return [...prev, uuid];
    });
  };

  const handleSelectAll = (uuids: string[]) => {
    setSelectedPersonUuids((prev) => {
      const allSelected = uuids.every((u) => prev.includes(u));
      if (allSelected) {
        return prev.filter((id) => !uuids.includes(id));
      }
      const set = new Set(prev);
      uuids.forEach((u) => set.add(u));
      return Array.from(set);
    });
  };

  const clearSelection = () => {
    setSelectedPersonUuids([]);
  };

  const handleAddThreema = async (personUuids: string[]) => {
    const uuidsToUpdate = personUuids;

    if (!threemaLicense || freeThreemaLicenseCount === 0) {
      showErrorToast(t('threema.notEnoughLicenses'));
      return;
    }
    const result = await updateProfileSettings({
      where: {
        uuid_IN: uuidsToUpdate,
      },
      update: {
        threemaUser: true,
      },
    });

    if (result.error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast(t('common.saved'));
      onSuccess?.();
    }

    clearSelection();
  };

  const handleDeleteThreema = async (personUuids: string[]) => {
    const confirm = await confirmConnectionDelete();
    if (!confirm) return;

    const responses = personUuids.map(async (uuid) => {
      return await updateProfileSettings({
        where: {
          uuid: uuid,
        },
        update: {
          threemaUser: false,
        },
      });
    });

    const results = await Promise.all(responses);
    const hasError = results.filter((r) => !!r.error).length > 0;

    if (hasError) {
      showUserDbErrorToast();
    } else {
      showSuccessToast(t('common.saved'));
      onSuccess?.();
    }

    clearSelection();
  };

  const handleBulkNextcloud = async (personUuids: string[]) => {
    const confirm = await confirmActivateNextcloud();
    if (!confirm) return;

    const result = await updateProfileSettings({
      where: {
        uuid_IN: personUuids,
      },
      update: {
        nextcloudUser: true,
      },
    });

    if (result.error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast(t('common.saved'));
      onSuccess?.();
    }

    clearSelection();
  };

  const handleDeleteNextcloud = async (personUuids: string[]) => {
    const confirm = await confirmConnectionDelete();
    if (!confirm) return;

    const responses = personUuids.map(async (uuid) => {
      return await updateProfileSettings({
        where: {
          uuid: uuid,
        },
        update: {
          nextcloudUser: false,
        },
      });
    });

    const results = await Promise.all(responses);
    const hasError = results.filter((r) => !!r.error).length > 0;

    if (hasError) {
      showUserDbErrorToast();
    } else {
      showSuccessToast(t('common.saved'));
      onSuccess?.();
    }

    clearSelection();
  };

  return {
    selectedPersonUuids,
    handlePersonSelect,
    handleSelectAll,
    clearSelection,
    handleAddThreema,
    handleDeleteThreema,
    handleBulkNextcloud,
    handleDeleteNextcloud,
    ConfirmationDialogs: () => (
      <>
        <ConnectionConfirmationDialog />
        <ActivateNextcloudConfirmationDialog />
      </>
    ),
  };
};
